import { useMemo, useState } from "react";

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery, type ListQueryPage } from "../../lib/useListQuery";
import { useResolvedWorkingTeamId } from "../../lib/workingScope";
import { useToast } from "../../ui";
import type { ScenarioSetFormSubmission } from "./ScenarioSetEditor";
import {
  ScenarioSetsListView,
  type ScenarioSetDrawerState,
} from "./ScenarioSetsListView";

type ScenarioSetCreate = components["schemas"]["ScenarioSetCreate"];
type ScenarioSetPatch = components["schemas"]["ScenarioSetPatch"];
type ScenarioSetResponse = components["schemas"]["ScenarioSetResponse"];
// The Scenario sets list + editor Drawer — the second tab of the top-level
// /scenarios surface. Scoped to one agent when the page carries `?agent=`,
// else the working team's list (`team_id`, docs/09 §4 — the personas
// pattern).
// Creating a scenario set requires a selected agent (docs/09 §4): its members
// are ordered scenarios OF ONE AGENT, so an unscoped create form would have to
// reload its member options per agent pick. The Add action is therefore only
// offered with an agent selected.
export interface ScenarioSetsListProps {
  // Present = the page carries an agent scope; absent = all scenario sets.
  agentId?: string;
  editingScenarioSetId: string | null;
  isCreateOpen: boolean;
  onCloseEditor: () => void;
  onOpenEdit: (scenarioSetId: string) => void;
}

export function ScenarioSetsList({
  agentId,
  editingScenarioSetId,
  isCreateOpen,
  onCloseEditor,
  onOpenEdit,
}: ScenarioSetsListProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deleteScenarioSetId, setDeleteScenarioSetId] = useState<string | null>(
    null,
  );
  // All agents = the working team's scenario sets (docs/09 §4); held until
  // the shell resolves the team. Agent wins: never both filters.
  const workingTeamId = useResolvedWorkingTeamId();
  const scenarioSetsListKey =
    agentId != null
      ? queryKeys.scenarioSets.list({ agent_id: agentId })
      : queryKeys.scenarioSets.list({ team_id: workingTeamId });
  const scenarioSets = useListQuery("/v1/scenario-sets", {
    enabled: agentId != null || workingTeamId !== undefined,
    params:
      agentId != null ? { agent_id: agentId } : { team_id: workingTeamId },
    queryKey: scenarioSetsListKey,
  });
  // Unscoped views resolve agent display names for the Agent column.
  const agents = useListQuery("/v1/agents", {
    enabled: agentId === undefined,
    queryKey: queryKeys.agents.list(),
  });
  const agentNamesById = useMemo(
    () => new Map(agents.items.map((agent) => [agent.id, agent.display_name])),
    [agents.items],
  );
  const detail = useQuery({
    enabled: editingScenarioSetId != null,
    queryKey: queryKeys.scenarioSets.detail(editingScenarioSetId ?? "pending"),
    queryFn: async () => {
      if (editingScenarioSetId == null) {
        throw new Error("Scenario set id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/scenario-sets/{id}", {
        params: { path: { id: editingScenarioSetId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    initialData: () =>
      queryClient
        .getQueryData<InfiniteData<ListQueryPage<"/v1/scenario-sets">>>(
          scenarioSetsListKey,
        )
        ?.pages.flatMap((page) => page.items)
        .find((scenarioSet) => scenarioSet.id === editingScenarioSetId),
  });
  // Member options are the scenarios of ONE agent: the scoped agent, or —
  // under All agents — the owning agent of the set being edited, which is
  // known only once the detail read resolves. Without either (an unscoped
  // page with no drawer open) there is no owner, so the read stays off.
  const memberOptionsAgentId = agentId ?? detail.data?.agent_id;
  const scenarios = useListQuery("/v1/scenarios", {
    enabled: memberOptionsAgentId !== undefined,
    pageSize: 200,
    params:
      memberOptionsAgentId === undefined
        ? {}
        : { agent_id: memberOptionsAgentId },
    queryKey: queryKeys.scenarios.list({ agent_id: memberOptionsAgentId }),
  });
  const drawer = useMemo<ScenarioSetDrawerState | null>(
    () =>
      isCreateOpen
        ? { mode: "create" }
        : editingScenarioSetId == null
          ? null
          : detail.data !== undefined
            ? agentId === undefined || detail.data.agent_id === agentId
              ? { mode: "edit", scenarioSet: detail.data }
              : {
                  message: "Scenario set not found for this agent.",
                  mode: "error",
                }
            : detail.isError
              ? {
                  message: requestErrorDescription(detail.error),
                  mode: "error",
                }
              : { mode: "loading" },
    [
      agentId,
      detail.data,
      detail.error,
      detail.isError,
      editingScenarioSetId,
      isCreateOpen,
    ],
  );
  const selectedScenarioIds = useMemo(
    () =>
      drawer?.mode === "edit"
        ? [...(drawer.scenarioSet.members ?? [])]
            .sort((left, right) => left.sort_order - right.sort_order)
            .map((member) => member.scenario_id)
        : [],
    [drawer],
  );
  const loadedScenarioIds = useMemo(
    () => new Set(scenarios.items.map((scenario) => scenario.id)),
    [scenarios.items],
  );
  const missingSelectedScenarioIds = selectedScenarioIds.filter(
    (scenarioId) => !loadedScenarioIds.has(scenarioId),
  );
  const selectedScenarioQueries = useQueries({
    queries: missingSelectedScenarioIds.map((scenarioId) => ({
      queryKey: queryKeys.scenarios.detail(scenarioId),
      queryFn: async () => {
        const { data, error } = await apiClient.GET("/v1/scenarios/{id}", {
          params: { path: { id: scenarioId } },
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        return data;
      },
    })),
  });
  const scenarioOptions = useMemo(() => {
    const byId = new Map(
      scenarios.items.map((scenario) => [
        scenario.id,
        { id: scenario.id, label: scenario.display_name },
      ]),
    );
    for (const query of selectedScenarioQueries) {
      if (query.data) {
        byId.set(query.data.id, {
          id: query.data.id,
          label: query.data.display_name,
        });
      }
    }
    return [...byId.values()];
  }, [scenarios.items, selectedScenarioQueries]);
  const selectedScenarioError = selectedScenarioQueries.find(
    (query) => query.isError,
  )?.error;

  const saveScenarioSet = useMutation({
    mutationFn: async ({
      scenarioSet,
      submission,
    }: {
      scenarioSet: ScenarioSetResponse | null;
      submission: ScenarioSetFormSubmission;
    }) => {
      const members = submission.scenarioIds.map((scenarioId, sortOrder) => ({
        scenario_id: scenarioId,
        sort_order: sortOrder,
      }));
      if (scenarioSet) {
        const body = {
          display_name: submission.displayName,
          members,
        } satisfies ScenarioSetPatch;
        const { data, error } = await apiClient.PATCH(
          "/v1/scenario-sets/{id}",
          { body, params: { path: { id: scenarioSet.id } } },
        );
        if (error) {
          throw new ApiRequestError(error);
        }
        return data;
      }
      if (agentId === undefined) {
        // Unreachable: the create action is offered only under an agent scope.
        throw new Error("Select an agent before creating a scenario set.");
      }
      const body = {
        agent_id: agentId,
        display_name: submission.displayName,
        members,
      } satisfies ScenarioSetCreate;
      const { data, error } = await apiClient.POST("/v1/scenario-sets", {
        body,
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (scenarioSet) => {
      onCloseEditor();
      queryClient.setQueryData(
        queryKeys.scenarioSets.detail(scenarioSet.id),
        scenarioSet,
      );
      toast.success("Scenario set saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.scenarioSets.all,
      });
    },
    onError: (error) => {
      toast.error("Scenario set save failed", {
        description: requestErrorDescription(error),
      });
    },
  });
  const deleteScenarioSet = useMutation({
    mutationFn: async (scenarioSet: ScenarioSetResponse) => {
      const { error } = await apiClient.DELETE("/v1/scenario-sets/{id}", {
        params: { path: { id: scenarioSet.id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return scenarioSet;
    },
    onSuccess: async () => {
      setDeleteScenarioSetId(null);
      toast.success("Scenario set deleted");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.scenarioSets.all,
      });
    },
    onError: (error) => {
      toast.error("Scenario set delete failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  return (
    <ScenarioSetsListView
      agentNamesById={agentNamesById}
      deleteError={
        deleteScenarioSet.isError
          ? requestErrorDescription(deleteScenarioSet.error)
          : null
      }
      deleteScenarioSetId={deleteScenarioSetId}
      drawer={drawer}
      hasNextScenarioPage={scenarios.hasNextPage}
      isDeletePending={deleteScenarioSet.isPending}
      isFetchingNextScenarioPage={scenarios.isFetchingNextPage}
      isHydratingSelectedScenarios={selectedScenarioQueries.some(
        (query) => query.isPending,
      )}
      isSavePending={saveScenarioSet.isPending}
      scenarioOptions={scenarioOptions}
      scenarioOptionsError={
        scenarios.errorMessage ??
        (selectedScenarioError
          ? requestErrorDescription(selectedScenarioError)
          : undefined)
      }
      scenarioSets={scenarioSets}
      scopedAgentId={agentId}
      saveError={
        saveScenarioSet.isError
          ? requestErrorDescription(saveScenarioSet.error)
          : null
      }
      onCancelDelete={() => {
        deleteScenarioSet.reset();
        setDeleteScenarioSetId(null);
      }}
      onCloseEditor={onCloseEditor}
      onConfirmDelete={(scenarioSet) => {
        deleteScenarioSet.mutate(scenarioSet);
      }}
      onLoadMoreScenarios={scenarios.fetchNextPage}
      onOpenEdit={onOpenEdit}
      onSave={async (submission) => {
        await saveScenarioSet.mutateAsync({
          scenarioSet: drawer?.mode === "edit" ? drawer.scenarioSet : null,
          submission,
        });
      }}
      onStartDelete={(scenarioSet) => {
        deleteScenarioSet.reset();
        setDeleteScenarioSetId(scenarioSet.id);
      }}
    />
  );
}
